import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Award, Users, Heart, Eye, TrendingUp, Palette } from 'lucide-react';

const AboutPage = () => {
  const milestones = [
    { year: "1976", title: "Gallery Founded", description: "Opened in July 1976 by Lee du Plooy in Hampstead village — a family gallery with a lifelong commitment to contemporary art." },
    { year: "1980s", title: "Post-War Focus", description: "Specialized in Post-War Modern Art, representing emerging and established contemporary artists." },
    { year: "2001", title: "New Leadership", description: "Gabrielle du Plooy takes ownership from her father, expanding art consultation and private sales services globally." },
    { year: "2010s", title: "Photography Archive", description: "Built renowned photography collection featuring iconic works and exclusive exhibitions." },
    { year: "2020s", title: "Women in Art", description: "Founded Women in Art Initiative to champion women artists over 55, sponsored by Riverstone." },
    { year: "Today", title: "Five Decades Strong", description: "Continuing to serve collectors worldwide with expertise in blue-chip contemporary art." }
  ];

  const values = [
    {
      icon: Eye,
      title: "Expertise",
      description: "Five decades of experience in post-war and contemporary art, from Warhol to Hockney."
    },
    {
      icon: Heart,
      title: "Personal Service",
      description: "We believe loving the artwork is the first investment. Every client receives tailored guidance."
    },
    {
      icon: Award,
      title: "Quality & Trust",
      description: "Vetted artworks, professional valuations, and conservation-level framing services."
    },
    {
      icon: Users,
      title: "Global Reach",
      description: "Working with collectors, institutions, and interior designers across the globe."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 border-b border-border">
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-6 font-sans">
              Established 1976
            </p>
            
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-foreground mb-8 leading-[1.1]">
              About Us
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl font-sans leading-relaxed mb-8">
              Fifty years on, Zebra One Gallery remains a cornerstone of contemporary 
              art in Hampstead — championing exceptional artists and serving collectors worldwide.
            </p>

            <p className="text-base text-muted-foreground max-w-3xl font-sans leading-relaxed">
              From our home in the heart of Hampstead village, we specialize in Post-War Modern Art, 
              Blue-Chip Contemporary Artists, Photography, and offering museum-level picture framing 
              and bespoke art consultation services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 px-6 bg-muted/20">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-6">
                Our Story
              </h2>
              
              <div className="space-y-6 text-muted-foreground font-sans leading-relaxed">
                <p>
                  Opened in July 1976 by Lee du Plooy, Zebra One Gallery began as 
                  a passion project in Hampstead — a space dedicated to celebrating 
                  contemporary art and providing exceptional framing services to 
                  the local community.
                </p>

                <p>
                  Over the decades, the gallery evolved into a respected name in the international art 
                  world, representing works by David Hockney, Andy Warhol, Jean-Michel Basquiat, 
                  Picasso, and many others. Our expertise spans paintings, prints, multiples, works on 
                  paper, and an extensive photography archive.
                </p>

                <p>
                  Twenty-five years ago, Lee passed the gallery to his daughter, Gabrielle du Plooy 
                  — a knowledgeable art dealer, framer, installer, and former artist. Under her 
                  ownership and direction, Zebra One continues to bridge collectors with exceptional 
                  artworks, manage private sales globally, and champion emerging talent through 
                  initiatives like the Women in Art award.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="aspect-[4/5] bg-muted/50 border border-border flex items-center justify-center"
            >
              <div className="text-center p-8">
                <Palette className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-sm uppercase tracking-wider text-muted-foreground font-sans">
                  Zebra One Gallery<br />Hampstead, London
                </p>
              </div>
            </motion.div>
          </div>

          {/* Timeline */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-12 text-center">
              Our Journey
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.year}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-background p-8 border border-border"
                >
                  <p className="text-4xl font-serif text-muted-foreground/30 mb-4">{milestone.year}</p>
                  <h3 className="font-serif text-xl text-foreground mb-3">{milestone.title}</h3>
                  <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                    {milestone.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Gabrielle du Plooy */}
      <section className="py-20 px-6 border-b border-border">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="aspect-square bg-muted/30 border border-border flex items-center justify-center"
            >
              <div className="text-center p-8">
                <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-sm uppercase tracking-wider text-muted-foreground font-sans">
                  Gabrielle du Plooy<br />Owner & Managing Director
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-6 font-sans">
                Leadership
              </p>
              
              <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-6">
                Gabrielle du Plooy
              </h2>
              
              <div className="space-y-6 text-muted-foreground font-sans leading-relaxed mb-8">
                <p>
                  Raised in the art world, Gabrielle brings a lifetime of expertise to Zebra One Gallery. 
                  As owner and managing director, she oversees private sales, artist representation, 
                  exhibitions, and global art consultancy services.
                </p>

                <p>
                  A knowledgeable art dealer, professional framer, installer, and former artist herself, 
                  Gabrielle is equally adept at guiding new and seasoned collectors through the art-buying 
                  process with personalized, professional service.
                </p>

                <p className="italic text-lg border-l-4 border-foreground pl-6">
                  "The end game for me is to give my clients the confidence to understand that to love 
                  the artwork is the first investment."
                </p>
              </div>

              <div className="bg-muted/30 p-6 border-l-4 border-foreground">
                <h3 className="font-serif text-lg text-foreground mb-2">Women in Art Initiative</h3>
                <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                  In partnership with Riverstone Living, Gabrielle founded the Women in Art Initiative—an 
                  award celebrating and supporting women artists over 55, providing them with exhibition 
                  opportunities and recognition.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 px-6 bg-muted/20">
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-6">
              What We Stand For
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto font-sans leading-relaxed">
              Our values have guided us for five decades—from our first day in Hampstead to serving 
              collectors across the globe.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-background p-8 border border-border group hover:border-foreground transition-colors"
                >
                  <Icon className="w-10 h-10 mb-6 text-foreground" />
                  
                  <h3 className="font-serif text-2xl text-foreground mb-4">
                    {value.title}
                  </h3>
                  
                  <p className="text-muted-foreground font-sans leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20 px-6 border-b border-border">
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-6">
              Our Services
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <TrendingUp className="w-12 h-12 mx-auto mb-6 text-foreground" />
              <h3 className="font-serif text-2xl text-foreground mb-4">Art Sales</h3>
              <p className="text-muted-foreground font-sans leading-relaxed mb-6">
                Browse our curated collection of contemporary art, from blue-chip artists to emerging talent.
              </p>
              <Link to="/shop" className="text-sm uppercase tracking-wider text-foreground underline hover:no-underline font-sans">
                Browse Collection
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-center"
            >
              <Palette className="w-12 h-12 mx-auto mb-6 text-foreground" />
              <h3 className="font-serif text-2xl text-foreground mb-4">Picture Framing</h3>
              <p className="text-muted-foreground font-sans leading-relaxed mb-6">
                Museum-level conservation framing with 7-day turnaround. Free advice and quotes.
              </p>
              <Link to="/picture-framing" className="text-sm uppercase tracking-wider text-foreground underline hover:no-underline font-sans">
                Framing Services
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center"
            >
              <Heart className="w-12 h-12 mx-auto mb-6 text-foreground" />
              <h3 className="font-serif text-2xl text-foreground mb-4">Art Consultation</h3>
              <p className="text-muted-foreground font-sans leading-relaxed mb-6">
                Bespoke art consultancy for collectors, interior designers, and developers worldwide.
              </p>
              <Link to="/art-consultation" className="text-sm uppercase tracking-wider text-foreground underline hover:no-underline font-sans">
                Learn More
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-foreground text-background">
        <div className="max-w-[1400px] mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-serif text-3xl md:text-4xl mb-6">
              Visit Us in Hampstead
            </h2>
            
            <p className="text-lg max-w-2xl mx-auto mb-12 font-sans leading-relaxed opacity-90">
              Experience five decades of art expertise in person. We're open Tuesday to Saturday, 
              10am–5pm, or by appointment.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-6 py-3 bg-background text-foreground font-sans text-sm uppercase tracking-[0.2em]"
                >
                  Get in Touch
                </motion.button>
              </Link>
              
              <Link to="/shop">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-6 py-3 border border-background text-background font-sans text-sm uppercase tracking-[0.2em]"
                >
                  Browse Collection
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
